import { useState } from 'react'
import { APP_SETTINGS } from '../../config/appSettings'
import { Icon } from '../../components/Icon'
import { TagLabel } from '../../components/TagLabel'

interface Props {
  id: string
  tags: readonly string[]
  selectedTag?: string
  selectedTags?: readonly string[]
  disabled?: boolean
  onSelect: (tag: string) => void
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  collapsible?: boolean
  preserveInputFocus?: boolean
}

/** One tag-filter surface for the rail and filter panel; callers own navigation. */
export function StudioTagPicker({ id, tags, selectedTag, selectedTags, disabled, onSelect, expanded, onExpandedChange, collapsible = false, preserveInputFocus = false }: Props) {
  const activeTags = selectedTags ?? (selectedTag ? [selectedTag] : [])
  const [sectionOpen, setSectionOpen] = useState(activeTags.length > 0)
  // A tag that is filtering the notes is always visible, so the filter can be seen and removed.
  const showTags = !collapsible || sectionOpen || activeTags.length > 0
  const [localExpanded, setLocalExpanded] = useState(false)
  const isExpanded = expanded ?? localExpanded
  // Active tags remain removable even if their last matching note disappeared.
  const availableTags = [...tags, ...activeTags.filter((tag) => !tags.includes(tag))]
  const preview = availableTags.slice(0, APP_SETTINGS.desktopStudio.tagPreviewMaxItems)
  activeTags.forEach((tag) => {
    if (!preview.includes(tag)) preview.push(tag)
  })
  const visibleTags = isExpanded ? availableTags : preview
  const hiddenCount = availableTags.length - preview.length
  const changeExpanded = () => {
    const next = !isExpanded
    setLocalExpanded(next)
    onExpandedChange?.(next)
  }
  return <section className="studio-tag-picker" aria-labelledby={`${id}-heading`}>
    <h2 id={`${id}-heading`} className={`studio-tag-picker__heading${collapsible ? ' studio-tag-picker__heading--collapsible' : ''}`}>
      {collapsible ? <button type="button" className="btn btn-ghost studio-tag-picker__disclosure" aria-expanded={showTags} aria-controls={`${id}-content`} disabled={disabled} onClick={() => setSectionOpen(open => !open)}><span>Tags</span><Icon name="chevron"/></button> : <><span>Tags</span><Icon name="tag"/></>}
    </h2>
    <div id={`${id}-content`} hidden={!showTags}>
    <div id={id} className="studio-tag-picker__items">
      {visibleTags.map(tag => <button key={tag} type="button" className="btn btn-ghost studio-tag-picker__tag" title={tag} aria-pressed={activeTags.includes(tag)} disabled={disabled} onPointerDown={preserveInputFocus ? event => event.preventDefault() : undefined} onClick={() => onSelect(tag)}><span className="studio-tag-picker__label"><TagLabel tag={tag}/></span></button>)}
    </div>
    {hiddenCount > 0 ? <button type="button" className="btn btn-ghost studio-collection-more studio-tag-picker__expand" aria-expanded={isExpanded} aria-controls={id} disabled={disabled} onClick={changeExpanded}><Icon name="chevron"/>{isExpanded ? 'Show fewer' : `Show ${hiddenCount} more`}</button> : null}
    </div>
  </section>
}
